import MedicalFilePill from '@/medical-common/components/MedicalFilePill'
import PageCitation from '@/medical-common/components/PageCitation'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import { useMatterAssetFeatureConfig } from '@/matter-asset-common/feature/matter-asset-feature-context'
import type { MatterAssetSourceRef } from '@/matter-asset-common/feature/matter-asset-feature-config'

interface AssetSourceFileCellProps {
  source: MatterAssetSourceRef
  pages: number[]
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
}

/**
 * Asset Source File Cell
 *
 * Table cell component for displaying:
 *  - A single source file pill - opens the file on the first page
 *  - A list of page chips - opens the file on the specified page
 *
 * Opening (and any file-pill / page-chip analytics) is delegated to the active feature config's
 * `useOpenSource`.
 */
export default function AssetSourceFileCell(props: AssetSourceFileCellProps) {
  const { source, pages, cellPosition, scrollToCell } = props
  const config = useMatterAssetFeatureConfig()
  const { openSourceFile, sourceLoadError } = config.useOpenSource()

  const sourceError = sourceLoadError?.sourceId === source.source_id ? sourceLoadError.message : null

  // V1 living assets only extract events from PDFs
  const openFile = async () => {
    scrollToCell(cellPosition)
    await openSourceFile(source)
  }

  const openFileOnPage = async (pageNumber: number) => {
    scrollToCell(cellPosition)
    await openSourceFile(source, { startingPage: pageNumber })
  }

  return (
    <div className="p-2 flex flex-col gap-2.5 w-full min-w-0">
      {/* Source file pill */}
      <div className="flex items-center gap-2">
        <MedicalFilePill fileName={source.display_name} onClick={openFile} />
        {sourceError && <span className="text-xs font-medium text-red-800">{sourceError}</span>}
      </div>

      {/* Page chips */}
      {pages.length > 0 && (
        <div className="flex flex-wrap gap-1 items-start">
          {pages.map((page: number, index: number) => (
            <PageCitation key={`page-${page}-${index}`} pages={[page]} onClick={() => openFileOnPage(page)} />
          ))}
        </div>
      )}
    </div>
  )
}
